<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import {
  mbcImport,
  mbcListLicenseProfileBundles,
  mbcListImportCapableProductTasks,
  mbcListImportCapableProductPolicy,
  mbcCapableProductTaskConfig,
  mbcListStartOfProductions,
  mbcGetSopByLpId,
  mbcGetFunctionsByLpId,
  mbcGetFuncFsIdBySopId,
  pmslistArticles,
  pmsGetProducts as pmsGetProductsApi,
  pmslistProducts,
  pmsGetScenarios,
  pmsGetActivations,
  pmsDeleteArticles,
  pmsDeleteProducts
} from '@/api/cfes/mbcPmsInformation'
import moment from 'moment-timezone'
import { toast } from '@/utils/toast'

const mbcTabs = ref('mbc-info')
const file = ref(null)
const uploadLoading = ref(false)
const searchLoading = ref(false)

const mbcImportErr = ref(false)
const importMsgInfo = ref('')
const conflictMsgInfo = ref([])
const conflictHeaders = ref([])

const lpId = ref('')
const lpName = ref('')
const articleId = ref('')

const page = ref(1)
const itemsPerPage = ref(10)
const totalItems = ref(0)
const inputPageValue = ref('')

const mbcPolicies = ref([])
const selectedPolicyId = ref(null)
const backendPolicyId = ref(null)

const mbcLicenseProfileBundlesSet = ref([])
const mbcStartOfProductionsSet = ref([])
const mbcImportTaskList = ref([])
const pmsArticlesSet = ref([])
const pmsProductsSet = ref([])

const expandedLps = ref([])
const expandedSops = ref([])
const expandedArticles = ref([])
const expandedProducts = ref([])

const deleteConfirmDialog = ref(false)
const deleteArticleId = ref(null)

const deleteProductConfirmDialog = ref(false)
const deleteProductId = ref(null)

// Feature flag: only enable PMS Article/Product delete buttons in non-prod environments.
// Controlled via VITE_FEATURE_PMS_DELETE in .env.iot / .env.preprod / .env.prod
const pmsDeleteEnabled = computed(() => {
  const v = import.meta.env.VITE_FEATURE_PMS_DELETE
  return v === true || v === 'true'
})

const lpHeaders = [
  { title: 'License Profile Bundle Id', key: 'lpId' },
  { title: 'Mod Article Id', key: 'mod4articleId' },
  { title: 'License Profile Bundle Name', key: 'lpName' },
  { title: 'Duration', key: 'duration' },
  { title: 'Created Time (UTC)', key: 'createdTime' },
  { title: 'Updated Time (UTC)', key: 'updatedTime' },
  { title: '', key: 'data-table-expand' },
]

const sopHeaders = [
  { title: 'SOP Id', key: 'sopId' },
  { title: 'SOP Name', key: 'sopName' },
  { title: 'Diagnosis Address', key: 'diagnosisAddress' },
  { title: '', key: 'data-table-expand' },
]

const taskHeaders = [
  { title: 'Task Id', key: 'taskId' },
  { title: 'Task Name', key: 'taskName' },
  { title: 'Task Policy', key: 'taskPolicy' },
  { title: 'Total Count', key: 'totalCount' },
  { title: 'Sent Count', key: 'sentCount' },
  { title: 'Estimated Completed Time', key: 'estimatedCompletedTime' },
  { title: 'Created Time (UTC)', key: 'createdTime' },
  { title: 'Updated Time (UTC)', key: 'updatedTime' }
]

const articleHeaders = [
  { title: 'Article Id', key: 'articleId' },
  { title: 'Article Name', key: 'articleName' },
  { title: 'Article Version', key: 'articleVersion' },
  { title: 'Brand Code', key: 'brandCode' },
  { title: 'Start of Life', key: 'startOfLife' },
  { title: 'End of Life', key: 'endOfLife' },
  { title: 'Created Time (UTC)', key: 'createdTime' },
  { title: 'Updated Time (UTC)', key: 'updatedTime' },
  { title: '', key: 'data-table-expand' },
]

const productHeaders = [
  { title: 'Product Id', key: 'productId' },
  { title: 'Product Name', key: 'productName' },
  { title: 'Product Version', key: 'productVersion' },
  { title: 'Product Type', key: 'productType' },
  { title: 'Created Time (UTC)', key: 'createdTime' },
  { title: 'Updated Time (UTC)', key: 'updatedTime' },
  { title: '', key: 'data-table-expand' },
]

const isPolicyUnchanged = computed(() => !selectedPolicyId.value || selectedPolicyId.value === backendPolicyId.value)
const pageCount = computed(() => {
  if (totalItems.value === 0 || itemsPerPage.value === 0) return 1
  return Math.ceil(totalItems.value / itemsPerPage.value)
})

const formatDate = (val) => val ? moment.utc(val).format("YYYY-MM-DD HH:mm:ss") : ''
const getRaw = (item) => item

const normalizeArray = (data, idKey, nameKey) => {
  if (!data) return []
  if (Array.isArray(data)) {
    return data.map(d => ({
      id: d[idKey] ?? d.id ?? '',
      name: d[nameKey] ?? d.name ?? ''
    }))
  }
  return Object.entries(data).map(([k, v]) => ({ id: k, name: v }))
}

const uploadFile = async () => {
  if (!file.value) {
    toast.warning('No File Selected!')
    return
  }
  uploadLoading.value = true
  const formData = new FormData()
  formData.append("file", file.value)

  try {
    await mbcImport(formData)
    toast.success('IMPORT SUCCESS!')
    loadLicenseProfiles('LICENSE PROFILE BUNDLE LIST UPDATED!')
  } catch (err) {
    if (err.data) {
      prepareConflictHeaders(err.msg || '')
      importMsgInfo.value = err.msg
      try {
        conflictMsgInfo.value = typeof err.data === 'string' ? JSON.parse(err.data) : err.data
      } catch (e) {
        conflictMsgInfo.value = []
      }
      mbcImportErr.value = true
    } else {
      toast.error('Import Failed')
    }
  } finally {
    uploadLoading.value = false
  }
}

const prepareConflictHeaders = (errMsg) => {
  let headers = []
  if (errMsg.includes("License") && errMsg.includes("repeatedly")) {
    headers.push({ title: 'License Bundle In File', key: 'licenseInFile' })
  } else if (errMsg.includes("mod4ArticleIds") && errMsg.includes("repeatedly")) {
    headers.push({ title: 'mod4ArticleIds In File', key: 'mod4ArticleId' })
  } else {
    headers.push(
      { title: 'mod4ArticleIds', key: 'mod4ArticleId' },
      { title: 'License Bundle In File', key: 'licenseInFile' },
      { title: 'License Bundle In DB', key: 'licenseInDB' }
    )
  }
  conflictHeaders.value = headers
}

const confirm_conflict = () => {
  mbcImportErr.value = false
  conflictMsgInfo.value = []
}

const loadLicenseProfiles = async (msg) => {
  searchLoading.value = true
  try {
    const res = await mbcListLicenseProfileBundles({
      lpId: lpId.value,
      lpName: lpName.value,
      articleId: articleId.value,
      page: page.value,
      row: itemsPerPage.value
    })
    if (res && res.data) {
      mbcLicenseProfileBundlesSet.value = res.data
      totalItems.value = res.totalElements
    } else {
       mbcLicenseProfileBundlesSet.value = res || []
       totalItems.value = res ? res.length : 0
    }
    if (msg) toast.success(msg)
  } catch (e) {
    console.error(e)
  } finally {
    searchLoading.value = false
  }
}

const searchData = () => {
  page.value = 1
  loadLicenseProfiles()
}

const goToPage = () => {
  if (/^[1-9]\d*$/.test(inputPageValue.value)) {
    page.value = parseInt(inputPageValue.value)
  } else {
    toast.warning('Page size error')
  }
}

const loadAllSupportingData = async () => {
  try {
    mbcStartOfProductionsSet.value = await mbcListStartOfProductions()
    pmsArticlesSet.value = await pmslistArticles()
    pmsProductsSet.value = await pmslistProducts()

    mbcPolicies.value = [
      { policyId: 'FAST_MODE', policyName: 'Fast Mode' },
      { policyId: 'BALANCED_MODE', policyName: 'Balanced Mode' }
    ]
    const pid = await mbcListImportCapableProductPolicy()
    const val = (typeof pid === 'object' && pid !== null && pid.policyId) ? pid.policyId : pid
    selectedPolicyId.value = val
    backendPolicyId.value = val

    mbcImportTaskList.value = await mbcListImportCapableProductTasks()
  } catch (e) {
    console.error(e)
  }
}

const savePolicy = async () => {
  if (isPolicyUnchanged.value) return
  try {
    await mbcCapableProductTaskConfig(selectedPolicyId.value)
    backendPolicyId.value = selectedPolicyId.value
    toast.success('Policy Saved')
  } catch (e) { toast.error('Failed to save policy') }
}

const confirmDeleteArticle = (artId) => {
  deleteArticleId.value = artId
  deleteConfirmDialog.value = true
}

const deleteArticle = async () => {
  deleteConfirmDialog.value = false
  if (!deleteArticleId.value) return
  try {
    await pmsDeleteArticles(deleteArticleId.value)
    toast.success('Article deleted successfully!')
    // Refresh articles list
    pmsArticlesSet.value = await pmslistArticles()
  } catch (e) {
    console.error(e)
    toast.error('Failed to delete article')
  } finally {
    deleteArticleId.value = null
  }
}

const confirmDeleteProduct = (prodId) => {
  deleteProductId.value = prodId
  deleteProductConfirmDialog.value = true
}

const deleteProduct = async () => {
  deleteProductConfirmDialog.value = false
  if (!deleteProductId.value) return
  try {
    await pmsDeleteProducts(deleteProductId.value)
    toast.success('Product deleted successfully!')
    pmsProductsSet.value = await pmslistProducts()
  } catch (e) {
    console.error(e)
    toast.error('Failed to delete product')
  } finally {
    deleteProductId.value = null
  }
}

watch(expandedLps, async (newVal) => {
  for (const id of newVal) {
    const item = mbcLicenseProfileBundlesSet.value.find(x => x.lpId === id)
    if (item && !item.sopSet) {
      try {
        const sops = await mbcGetSopByLpId(item.lpId)
        const funcs = await mbcGetFunctionsByLpId(item.lpId)
        item.sopSet = Array.isArray(sops) ? sops : normalizeArray(sops, 'sopId', 'sopName')
        item.sopCount = item.sopSet.length
        item.funcSet = Array.isArray(funcs) ? funcs : normalizeArray(funcs, 'functionId', 'functionName')
        item.funcCount = item.funcSet.length
      } catch (e) { console.error(e) }
    }
  }
})

watch(expandedSops, async (newVal) => {
  for (const id of newVal) {
    const item = mbcStartOfProductionsSet.value.find(x => x.sopId === id)
    if (item && !item.functionIdFsIdSet) {
      try {
        const res = await mbcGetFuncFsIdBySopId(item.sopId)
        item.functionIdFsIdSet = Array.isArray(res) ? res : []
      } catch (e) { console.error(e) }
    }
  }
})

watch(expandedArticles, async (newVal) => {
  for (const id of newVal) {
    const item = pmsArticlesSet.value.find(x => x.articleId === id)
    if (item && !item.prodSet) {
      try {
        const prods = await pmsGetProductsApi(item.articleId)
        const scns = await pmsGetScenarios(item.articleId)
        item.prodSet = Array.isArray(prods) ? prods : normalizeArray(prods, 'productId', 'productName')
        item.prodCount = item.prodSet.length
        item.scnSet = Array.isArray(scns) ? scns : normalizeArray(scns, 'scenarioType', 'scenarioName')
        item.scnCount = item.scnSet.length
      } catch (e) { console.error(e) }
    }
  }
})

watch(expandedProducts, async (newVal) => {
  for (const id of newVal) {
    const item = pmsProductsSet.value.find(x => x.productId === id)
    if (item && !item.activationSet) {
      try {
        const res = await pmsGetActivations(item.productId)
        item.activationSet = Array.isArray(res) ? res : normalizeArray(res, 'activationId', 'status')
        item.activationCount = item.activationSet.length
      } catch (e) { console.error(e) }
    }
  }
})

watch([page, itemsPerPage], () => loadLicenseProfiles())

const store = useStore()
const currentBrand = computed(() => store.getters.brand)
const currentRegion = computed(() => store.getters.region)

// Reset all local state and reload data when brand or region changes
const reloadAll = () => {
  // Reset search filters & pagination
  lpId.value = ''
  lpName.value = ''
  articleId.value = ''
  page.value = 1
  inputPageValue.value = ''

  // Clear all tables / expanded rows so stale (previous brand/region) data is not shown
  mbcLicenseProfileBundlesSet.value = []
  mbcStartOfProductionsSet.value = []
  mbcImportTaskList.value = []
  pmsArticlesSet.value = []
  pmsProductsSet.value = []
  expandedLps.value = []
  expandedSops.value = []
  expandedArticles.value = []
  expandedProducts.value = []
  totalItems.value = 0

  loadLicenseProfiles()
  loadAllSupportingData()
}

watch([currentBrand, currentRegion], ([nb, nr], [ob, or]) => {
  if (nb === ob && nr === or) return
  if (!nb || !nr) return
  reloadAll()
})

onMounted(() => {
  loadLicenseProfiles()
  loadAllSupportingData()
})
</script>

<template>
  <v-container fluid class="page-shell">
    <v-row class="text-center mb-4">
      <v-col cols="12">
        <h1 class="title font-weight-bold">MBC & PMS Information</h1>
        <div class="headline-border"></div>
      </v-col>
    </v-row>

    <v-tabs v-model="mbcTabs" class="mb-4" density="comfortable">
      <v-tab value="mbc-info">MBC Information</v-tab>
      <v-tab value="mbc-task">MBC Import Capable Product Task</v-tab>
      <v-tab value="pms-info">PMS Information</v-tab>
    </v-tabs>

    <v-window v-model="mbcTabs">
      <v-window-item value="mbc-info">

        <!-- Import Section -->
        <v-card class="section-card mb-4" v-permission="['mbc:information:import']">
          <v-card-title class="font-weight-bold subtitle-2">Import MBC File</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-file-input
                  v-model="file"
                  label="File input"
                  show-size
                  density="compact"
                  variant="outlined"
                  accept=".json"
                  prepend-icon="mdi-paperclip"
                  hide-details
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-btn size="small" color="primary" :loading="uploadLoading" @click="uploadFile">UPLOAD</v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Search Section -->
        <v-card class="section-card mb-4">
          <v-card-title class="font-weight-bold subtitle-2">Search Filters</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field v-model="lpId" label="License Profile Bundle Id" density="compact" hide-details variant="outlined" />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="lpName" label="License Profile Bundle Name" density="compact" hide-details variant="outlined" />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="articleId" label="Article Id" density="compact" hide-details variant="outlined" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-btn size="small" color="primary" :loading="searchLoading" @click="searchData">SEARCH</v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- License Profile Bundle Table -->
        <v-card class="section-card mb-4">
          <v-card-title class="font-weight-bold subtitle-2">License Profile Bundle Information</v-card-title>
          <v-data-table-server
            v-model:expanded="expandedLps"
            class="monitoring-table mb-2"
            density="compact"
            hide-default-footer
            :headers="lpHeaders"
            :items="mbcLicenseProfileBundlesSet"
            :items-length="totalItems"
            :items-per-page="itemsPerPage"
            show-expand
            expand-on-click
            item-value="lpId"
          >
            <template #item.createdTime="{ item }">{{ formatDate(getRaw(item).createdTime) }}</template>
            <template #item.updatedTime="{ item }">{{ formatDate(getRaw(item).updatedTime) }}</template>

            <template #expanded-row="{ columns, item }">
              <tr>
                <td :colspan="columns.length" class="bg-grey-lighten-4 pa-3">
                  <v-card flat class="bg-transparent">
                    <v-row dense>
                      <v-col cols="12" md="6">
                        <div class="subtitle-2 font-weight-bold mb-1 ml-2">
                          SOP ID ({{ getRaw(item).sopCount || 0 }})
                        </div>
                        <v-table density="compact" class="nested-table">
                          <tbody>
                                <tr v-for="s in getRaw(item).sopSet || []" :key="s.sopId || s.id">
                                  <td>{{ s.sopId || s.id }}</td>
                            </tr>
                            <tr v-if="!getRaw(item).sopSet || getRaw(item).sopSet.length === 0">
                                  <td colspan="2" class="text-caption text-grey text-center">No Data</td>
                            </tr>
                          </tbody>
                        </v-table>
                      </v-col>
                      <v-col cols="12" md="6">
                        <div class="subtitle-2 font-weight-bold mb-1 ml-2">
                          Function ID ({{ getRaw(item).funcCount || 0 }})
                        </div>
                        <v-table density="compact" class="nested-table">
                          <tbody>
                                <tr v-for="f in getRaw(item).funcSet || []" :key="f.functionId || f.id">
                                  <td>{{ f.functionId || f.id }}</td>
                            </tr>
                            <tr v-if="!getRaw(item).funcSet || getRaw(item).funcSet.length === 0">
                               <td colspan="2" class="text-center">No Data</td>
                            </tr>
                          </tbody>
                        </v-table>
                      </v-col>
                    </v-row>
                  </v-card>
                </td>
              </tr>
            </template>
          </v-data-table-server>

          <v-toolbar flat density="compact" class="mt-n2 bg-transparent">
            <v-spacer />
            <v-pagination v-model="page" :length="pageCount" total-visible="5" density="compact" class="mr-2" />
            <div style="width: 70px" class="mr-1">
              <v-text-field v-model="inputPageValue" density="compact" hide-details type="number" variant="outlined" />
            </div>
            <v-btn icon="mdi-chevron-right" variant="text" size="small" @click="goToPage"></v-btn>
          </v-toolbar>
        </v-card>

        <!-- SOP Table -->
        <v-card class="section-card">
          <v-card-title class="font-weight-bold subtitle-2">Start of Production Information</v-card-title>
          <v-data-table
            v-model:expanded="expandedSops"
            :headers="sopHeaders"
            :items="mbcStartOfProductionsSet"
            item-value="sopId"
            show-expand
            expand-on-click
            density="compact"
            class="monitoring-table"
            hide-default-footer
            :items-per-page="-1"
          >
            <template #expanded-row="{ columns, item }">
              <tr>
                <td :colspan="columns.length" class="bg-grey-lighten-4 pa-3">
                    <v-card flat class="bg-transparent" max-width="800">
                      <div class="subtitle-2 font-weight-bold mb-1 ml-2">Funtion / FS ID Mapping</div>
                        <v-table density="compact" class="nested-table">
                          <thead><tr><th>Function ID</th><th>FS ID</th></tr></thead>
                          <tbody>
                                <tr v-for="(fItem, index) in (getRaw(item).functionIdFsIdSet || [])" :key="index">
                                  <td>{{ fItem.functionId }}</td>
                                  <td>{{ fItem.fsId }}</td>
                            </tr>
                                <tr v-if="!getRaw(item).functionIdFsIdSet || getRaw(item).functionIdFsIdSet.length === 0">
                                   <td colspan="2" class="text-caption text-grey text-center">No Data</td>
                            </tr>
                          </tbody>
                        </v-table>
                    </v-card>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>

        <v-dialog v-model="mbcImportErr" max-width="600">
          <v-card>
            <v-card-title class="bg-red text-white">Import Error</v-card-title>
            <v-card-text class="pt-4">
              <v-alert type="error" variant="tonal" class="mb-2">{{ importMsgInfo }}</v-alert>
              <v-data-table :headers="conflictHeaders" :items="conflictMsgInfo" density="compact"
                height="300"></v-data-table>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn size="small" color="primary" @click="confirm_conflict">Confirm</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-window-item>

      <v-window-item value="mbc-task">
        <!-- Configuration Card Top -->
        <v-card class="section-card mb-4">
          <v-card-title class="font-weight-bold subtitle-2">Configuration</v-card-title>
          <v-card-text>
             <v-row>
                <v-col cols="12">
                  <v-radio-group v-model="selectedPolicyId" hide-details inline density="compact">
                    <v-radio v-for="p in mbcPolicies" :key="p.policyId" :label="p.policyName" :value="p.policyId" class="mr-4" />
                  </v-radio-group>
                </v-col>
             </v-row>
             <v-row>
                <v-col cols="12" v-permission="['mbc:information:import']">
                   <v-btn size="small" color="primary" :disabled="isPolicyUnchanged" @click="savePolicy">Save</v-btn>
                </v-col>
             </v-row>
          </v-card-text>
        </v-card>

        <!-- Task Table Bottom -->
        <v-card class="section-card">
          <v-card-title class="font-weight-bold subtitle-2">MBC Import Capable Product Task</v-card-title>
          <v-data-table :headers="taskHeaders" :items="mbcImportTaskList" density="compact" class="monitoring-table" hide-default-footer :items-per-page="-1">
            <template #item.createdTime="{ item }">{{ formatDate(getRaw(item).createdTime) }}</template>
            <template #item.updatedTime="{ item }">{{ formatDate(getRaw(item).updatedTime) }}</template>
          </v-data-table>
        </v-card>
      </v-window-item>

      <v-window-item value="pms-info">
          <v-card class="section-card mb-4">
            <v-card-title class="font-weight-bold subtitle-2">Article Information</v-card-title>
            <v-data-table
              v-model:expanded="expandedArticles"
              :headers="articleHeaders"
              :items="pmsArticlesSet"
              item-value="articleId"
              show-expand
              expand-on-click
              density="compact"
              class="monitoring-table"
              hide-default-footer
              :items-per-page="-1"
            >
              <template #item.createdTime="{ item }">{{ formatDate(getRaw(item).createdTime) }}</template>
              <template #item.updatedTime="{ item }">{{ formatDate(getRaw(item).updatedTime) }}</template>
              <template #expanded-row="{ columns, item }">
                <tr>
                  <td :colspan="columns.length" class="bg-grey-lighten-4 pa-3">
                    <v-card flat class="bg-transparent">
                        <v-row dense>
                          <v-col cols="12" md="6">
                            <div class="subtitle-2 font-weight-bold mb-1 ml-2">
                                Product ID ({{ getRaw(item).prodCount || 0 }})
                            </div>
                            <v-table density="compact" class="nested-table">
                              <tbody>
                                    <tr v-for="p in getRaw(item).prodSet || []" :key="p.productId || p.id">
                                       <td>{{ p.productId || p.id }}</td>
                                </tr>
                                <tr v-if="!getRaw(item).prodSet || getRaw(item).prodSet.length === 0">
                                   <td colspan="2" class="text-center">No Data</td>
                                </tr>
                              </tbody>
                            </v-table>
                          </v-col>
                          <v-col cols="12" md="6">
                            <div class="subtitle-2 font-weight-bold mb-1 ml-2">
                                Scenario Type ({{ getRaw(item).scnCount || 0 }})
                            </div>
                            <v-table density="compact" class="nested-table">
                               <tbody>
                                    <tr v-for="s in getRaw(item).scnSet || []" :key="s.scenarioType || s.id">
                                       <td>{{ s.scenarioType }}</td>
                                  </tr>
                                  <tr v-if="!getRaw(item).scnSet || getRaw(item).scnSet.length === 0">
                                      <td colspan="2" class="text-caption text-grey text-center">No Data</td>
                                  </tr>
                               </tbody>
                            </v-table>
                          </v-col>
                        </v-row>
                        <v-row dense class="mt-2" v-if="pmsDeleteEnabled">
                          <v-col cols="12">
                            <v-btn size="x-small" color="error" variant="outlined" @click.stop="confirmDeleteArticle(getRaw(item).articleId)">Delete Article</v-btn>
                          </v-col>
                        </v-row>
                    </v-card>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card>

          <v-card class="section-card">
            <v-card-title class="font-weight-bold subtitle-2">Product Information</v-card-title>
            <v-data-table
              v-model:expanded="expandedProducts"
              :headers="productHeaders"
              :items="pmsProductsSet"
              item-value="productId"
              show-expand
              expand-on-click
              density="compact"
              class="monitoring-table"
              hide-default-footer
              :items-per-page="-1"
             >
              <template #item.createdTime="{ item }">{{ formatDate(getRaw(item).createdTime) }}</template>
              <template #item.updatedTime="{ item }">{{ formatDate(getRaw(item).updatedTime) }}</template>
              <template #expanded-row="{ columns, item }">
                <tr>
                   <td :colspan="columns.length" class="bg-grey-lighten-4 pa-3">
                      <v-card flat class="bg-transparent" max-width="600">
                         <div class="subtitle-2 font-weight-bold mb-1 ml-2">
                             Activation ID ({{ getRaw(item).activationCount || 0 }})
                         </div>
                         <v-table density="compact" class="nested-table">
                            <tbody>
                                 <tr v-for="a in getRaw(item).activationSet || []" :key="a.activationId || a.id">
                                    <td>{{ a.activationId || a.id }}</td>
                               </tr>
                               <tr v-if="!getRaw(item).activationSet || getRaw(item).activationSet.length === 0">
                                     <td colspan="2" class="text-caption text-grey text-center">No Data</td>
                               </tr>
                            </tbody>
                         </v-table>
                         <v-row dense class="mt-2" v-if="pmsDeleteEnabled">
                           <v-col cols="12">
                             <v-btn size="x-small" color="error" variant="outlined" @click.stop="confirmDeleteProduct(getRaw(item).productId)">Delete Product</v-btn>
                           </v-col>
                         </v-row>
                      </v-card>
                   </td>
                </tr>
              </template>
            </v-data-table>
          </v-card>

          <!-- Delete Article Confirmation Dialog -->
          <v-dialog v-model="deleteConfirmDialog" max-width="400" persistent>
            <v-card>
              <v-card-title class="text-h6">Confirm Delete</v-card-title>
              <v-card-text>Are you sure you want to delete article <strong>{{ deleteArticleId }}</strong>?</v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn size="small" @click="deleteConfirmDialog = false">Cancel</v-btn>
                <v-btn size="small" color="error" @click="deleteArticle">Delete</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- Delete Product Confirmation Dialog -->
          <v-dialog v-model="deleteProductConfirmDialog" max-width="400" persistent>
            <v-card>
              <v-card-title class="text-h6">Confirm Delete</v-card-title>
              <v-card-text>Are you sure you want to delete product <strong>{{ deleteProductId }}</strong>?</v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn size="small" @click="deleteProductConfirmDialog = false">Cancel</v-btn>
                <v-btn size="small" color="error" @click="deleteProduct">Delete</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
      </v-window-item>

    </v-window>

  </v-container>
</template>

<style scoped>
.page-shell {
  padding: 8px 0 28px;
}

.title {
  font-size: 1.1rem;
}

.headline-border {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #4f46e5, #06b6d4);
  margin: 12px auto 0;
  border-radius: 999px;
}

.section-card {
  border: 1px solid #e5e7eb;
  border-radius: 0;
  background: #fff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.05);
  padding: 10px 12px 14px;
}

.monitoring-table :deep(.v-data-table__wrapper) {
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.monitoring-table :deep(tbody tr:nth-child(odd)) {
  background-color: #f9fafb;
}

.monitoring-table :deep(tbody tr:hover) {
  background-color: #eef2ff;
}

.nested-table {
  background-color: transparent !important;
}

.nested-table :deep(th) {
  font-size: 0.75rem !important;
  font-weight: 600;
  color: #666;
}

.nested-table :deep(td) {
  font-size: 0.75rem !important;
}

:deep(.v-field__input), :deep(.v-label) {
  font-size: 0.75rem !important;
}

:deep(.v-btn) {
  text-transform: none;
}
</style>
